import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { deleteChunk, listChunks, putChunk } from "@/lib/localStore";

function upload(id, blob, index, offset, track = "room") {
  const fd = new FormData();
  fd.append("file", blob, `chunk_${index}_${track}.webm`);
  fd.append("index", String(index));
  fd.append("offset", String(offset));
  fd.append("track", track);
  return api.post(`/sessions/${id}/chunks`, fd, { timeout: 60000 });
}

const permanent = (e) => {
  const st = e?.response?.status;
  return st && st >= 400 && st < 500 && st !== 408 && st !== 429;
};

// Every chunk goes to IndexedDB first, then uploads; failed uploads stay queued and retry when the network returns.
// With memoryOnly (private meetings) chunks never touch IndexedDB: a dropped connection can lose audio, but nothing is kept on disk.
export function useChunkQueue(id, memoryOnly = false) {
  const [queued, setQueued] = useState(0);
  const [uplinkOk, setUplinkOk] = useState(true);
  const busy = useRef(false);
  const mem = useRef(new Map());
  const memoryRef = useRef(memoryOnly);
  memoryRef.current = memoryOnly;
  const store = useRef({
    put: (c) => (memoryRef.current ? Promise.resolve(mem.current.set(c.key, c)) : putChunk(c)),
    list: (sid) => (memoryRef.current ? Promise.resolve([...mem.current.values()]) : listChunks(sid)),
    del: (key) => (memoryRef.current ? Promise.resolve(mem.current.delete(key)) : deleteChunk(key)),
  }).current;

  const flush = useCallback(async () => {
    if (busy.current) return;
    busy.current = true;
    try {
      const items = (await store.list(id)).sort((a, b) => a.index - b.index);
      setQueued(items.length);
      for (const c of items) {
        try {
          await upload(id, c.blob, c.index, c.offset, c.track);
        } catch (e) {
          if (!permanent(e)) {
            setUplinkOk(false);
            break;
          }
          if (e.response.status !== 409 && e.response.status !== 404) toast.error(`Chunk ${c.index}: ${errMsg(e)}`);
        }
        await store.del(c.key);
        setQueued((q) => Math.max(0, q - 1));
        setUplinkOk(true);
      }
    } catch {
      /* IndexedDB unavailable */
    } finally {
      busy.current = false;
    }
  }, [id, store]);

  const enqueue = useCallback(async (blob, index, offset, track = "room") => {
    try {
      await store.put({ key: `${id}:${index}:${track}`, sid: id, index, offset, track, blob, created: Date.now() });
      setQueued((q) => q + 1);
    } catch {
      return upload(id, blob, index, offset, track).catch((e) => toast.error(`Chunk ${index} lost: ${errMsg(e)}`));
    }
    return flush();
  }, [id, flush, store]);

  // A chunk the browser heard no speech in: only its place on the timeline is reported, no audio is sent.
  // Best effort: losing one of these only leaves a quiet stretch out of the stats.
  const markSilent = useCallback((index, offset, duration, track = "room") => {
    if (!(duration > 0)) return;
    api.post(`/sessions/${id}/chunks/${index}/silent`, { offset, duration, track }).catch(() => {});
  }, [id]);

  useEffect(() => {
    flush();
    const iv = setInterval(flush, 5000);
    window.addEventListener("online", flush);
    return () => {
      clearInterval(iv);
      window.removeEventListener("online", flush);
    };
  }, [flush]);

  return { enqueue, markSilent, queued, uplinkOk };
}
